import { CardBody, Input } from '../../../../_metronic/_partials/controls';
import { Row, Col, InputGroup, Spinner } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import * as actions from '../../../../store/actions/index';
import { useEffect, useState, useContext } from 'react';
import { useReadContract } from 'wagmi';
import { ether } from '../../../../helpers/ethers';
import { abi } from '../../../../config/abi';
import { useAppKit } from '@reown/appkit/react';
import { connectionContext } from '../../../connect-wallet/ConnectionStatus';
import { ethers } from 'ethers';
import Select from 'react-select';

import useContractInteraction from '../../../../helpers/useContractInteraction';
import { toast } from 'react-toastify';

import './CryptoRevenueForm.scss';

export const CryptoRevenueForm = () => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const { open } = useAppKit();

  const { address, isConnected, isReconnecting, isConnecting } =
    useContext(connectionContext);

  // extract payments safely and default to array
  const { payments: rawPayments } = useSelector(
    (state) => state.paymentGateway,
  );
  const payments = Array.isArray(rawPayments) ? rawPayments : [];

  const { taxonomy } = useSelector((state) => state?.taxonomy);
  const { currentPermissions } = useSelector((state) => state.role);
  const blockchainOption = payments.filter(Boolean).map((option) => ({
    value: option.chainId,
    label: option.title,
  }));

  const [currencySymbol, setCurrenctSymbol] = useState('ETH');

  const [selectedChainId, setSelectedChainId] = useState(
    blockchainOption.length > 0 ? blockchainOption[0].value : 560048,
  );

  const [currentPlatformFees, setCurrentPlatformFees] = useState({
    percentageCampaignCommission: '0',
    fixedCampaignCommission: '0.00',
    percentageSuccessfulCommission: '0',
    fixedSuccessfulCommission: '0.00',
    percentageUnsuccessfulCommission: '0',
    fixedUnsuccessfulCommission: '0.00',
    percentageInvestmentFees: '0',
    fixedInvestmentFees: '0.00',
    percentageGatewayFees: '0',
    fixedGatewayFees: '0.00',
  });

  const [contractDetails, setContractDetails] = useState({
    address: payments[0]?.contractAddress || '',
    chainId: selectedChainId || '',
  });

  const {
    data: platformFees,
    error: platformFeesError,
    refetch: refetchPlatformFees,
  } = useReadContract({
    address: contractDetails.address,
    chainId: Number(contractDetails.chainId),
    abi: abi,
    functionName: 'getPlatformFees',
    query: {
      enabled: !!contractDetails.address && !!contractDetails.chainId,
    },
  });

  const { isLoading, isSuccess, error, interact, data } =
    useContractInteraction();

  const percentage = Yup.number()
    .positive()
    .label(intl.formatMessage({ id: 'PERCENTAGE.ERROR.MESSAGE' }))
    .max(100)
    .required();

  const fixed = Yup.number()
    .positive()
    .required()
    .label(intl.formatMessage({ id: 'FIXED.ERROR.MESSAGE' }));

  const initialValues = {
    chainId:
      blockchainOption.length > 0 ? blockchainOption[0].value : undefined,
    percentageCampaignCommission:
      currentPlatformFees.percentageCampaignCommission || '0',
    fixedCampaignCommission:
      currentPlatformFees.fixedCampaignCommission || '0.00',
    percentageSuccessfulCommission:
      currentPlatformFees.percentageSuccessfulCommission || '0',
    fixedSuccessfulCommission:
      currentPlatformFees.fixedSuccessfulCommission || '0.00',
    percentageUnsuccessfulCommission:
      currentPlatformFees.percentageUnsuccessfulCommission || '0',
    fixedUnsuccessfulCommission:
      currentPlatformFees.fixedUnsuccessfulCommission || '0.00',
    percentageInvestmentFees:
      currentPlatformFees.percentageInvestmentFees || '0',
    fixedInvestmentFees: currentPlatformFees.fixedInvestmentFees || '0.00',
    percentageGatewayFees: currentPlatformFees.percentageGatewayFees || '0',
    fixedGatewayFees: currentPlatformFees.fixedGatewayFees || '0.00',
  };

  const validationSchema = Yup.object().shape({
    investType: Yup.string(),
    percentageCampaignCommission: percentage,
    fixedCampaignCommission: fixed,
    percentageSuccessfulCommission: percentage,
    fixedSuccessfulCommission: fixed,
    percentageUnsuccessfulCommission: percentage,
    fixedUnsuccessfulCommission: fixed,
    percentageInvestmentFees: percentage,
    fixedInvestmentFees: fixed,
    percentageGatewayFees: percentage,
    fixedGatewayFees: fixed,
  });

  useEffect(() => {
    dispatch(actions.getPaymentGateway('crypto-payment'));
  }, []);

  useEffect(() => {
    const currentChain = payments.find(
      (payment) => payment.chainId === Number(selectedChainId),
    );

    if (currentChain) {
      setCurrenctSymbol(currentChain.currencyCode);
      setContractDetails({
        address: currentChain.contractAddress,
        chainId: selectedChainId,
      });
    }
  }, [selectedChainId, payments]);

  useEffect(() => {
    if (platformFees) {
      setCurrentPlatformFees({
        fixedCampaignCommission: ether.formatEther(platformFees[0]),
        percentageCampaignCommission: ether.formatContractPercentageFrom(
          platformFees[1],
        ),
        percentageSuccessfulCommission: ether.formatContractPercentageFrom(
          platformFees[3],
        ),
        fixedSuccessfulCommission: ether.formatEther(platformFees[2]),
        fixedUnsuccessfulCommission: ether.formatEther(platformFees[4]),
        percentageUnsuccessfulCommission: ether.formatContractPercentageFrom(
          platformFees[5],
        ),
        fixedInvestmentFees: ether.formatEther(platformFees[6]),
        percentageInvestmentFees: ether.formatContractPercentageFrom(
          platformFees[7],
        ),
        fixedGatewayFees: ether.formatEther(platformFees[8]),
        percentageGatewayFees: ether.formatContractPercentageFrom(
          platformFees[9],
        ),
      });
    }
    if (platformFeesError) {
      toast.error(platformFeesError.details);
    }
  }, [platformFees, platformFeesError]);

  const handleSubmitForm = async (values) => {
    if (!isConnected) {
      return open();
    } else {
      await interact(
        ethers.getAddress(contractDetails.address),
        abi,
        'setPlatformFees',
        [
          ether.formatContractPercentageTo(values.percentageCampaignCommission),
          ether.parseEther(values.fixedCampaignCommission),
          ether.formatContractPercentageTo(
            values.percentageSuccessfulCommission,
          ),
          ether.parseEther(values.fixedSuccessfulCommission),
          ether.formatContractPercentageTo(
            values.percentageUnsuccessfulCommission,
          ),
          ether.parseEther(values.fixedUnsuccessfulCommission),
          ether.formatContractPercentageTo(values.percentageInvestmentFees),
          ether.parseEther(values.fixedInvestmentFees),
          ether.formatContractPercentageTo(values.percentageGatewayFees),
          ether.parseEther(values.fixedGatewayFees),
        ],
        values.chainId,
        ethers.getAddress(address),
      );
    }
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success(intl.formatMessage({ id: 'SETTING.SUCCESS' }));
      refetchPlatformFees();
    }
  }, [isSuccess]);

  return (
    <CardBody className='px-4 pt-0 py-6'>
      <Row className='justify-content-md-center'>
        <Col md={11}>
          <Formik
            enableReinitialize={true}
            initialValues={{
              ...initialValues,
              chainId: selectedChainId,
            }}
            validationSchema={validationSchema}
          >
            {({ isValid, isSubmitting, values, setFieldValue }) => {
              return (
                <Form className='form form-label-right'>
                  <div className='form-group floating-input-field-wrapper mb-5'>
                    <label
                      htmlFor='chainId'
                      className='floating-input-field-label-static ms-4 start-0'
                    >
                      {intl.formatMessage({
                        id: 'BLOCKCHAIN.SELECT',
                      })}
                    </label>
                    <Select
                      name='chainId'
                      options={blockchainOption}
                      classNamePrefix='react-select'
                      value={blockchainOption.find(
                        (option) => option.value === values.chainId,
                      )} // Formik value
                      onChange={(selectedOption) => {
                        setFieldValue('chainId', selectedOption?.value); // update Formik
                        setSelectedChainId(selectedOption?.value); // update local state
                      }}
                      placeholder={intl.formatMessage({
                        id: 'BLOCKCHAIN.SELECT',
                      })}
                    />

                    <Field type='hidden' name='chainId' />
                  </div>

                  <div className='form-group'>
                    <label className='text-black font-size-sm'>
                      {intl.formatMessage(
                        { id: 'FIXED.COMMISSION' },
                        {
                          value: taxonomy?.projectSingular,
                        },
                      )}
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageCampaignCommission'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='percentageCampaignCommission'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='percentageCampaignCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedCampaignCommission'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${currencySymbol})`}</InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='fixedCampaignCommission'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedCampaignCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='font-size-sm text-black'>
                      {intl.formatMessage(
                        {
                          id: `FLEX.SUCC.CAMP`,
                        },
                        {
                          value: taxonomy?.projectSingular,
                        },
                      )}
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageSuccessfulCommission'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='percentageSuccessfulCommission'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='percentageSuccessfulCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedSuccessfulCommission'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${currencySymbol})`}</InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='fixedSuccessfulCommission'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedSuccessfulCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='font-size-sm text-black'>
                      {intl.formatMessage(
                        { id: 'FLEX.FAIL.CAMP' },
                        {
                          value: taxonomy?.projectSingular,
                        },
                      )}
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageUnsuccessfulCommission'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='percentageUnsuccessfulCommission'
                            component={Input}
                            withFeedbackLabel={false}
                            className='rounded-start-0 text-black'
                          />
                          <ErrorMessage
                            name='percentageUnsuccessfulCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedUnsuccessfulCommission'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${currencySymbol})`}</InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            className='rounded-start-0 text-black'
                            name='fixedUnsuccessfulCommission'
                            component={Input}
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedUnsuccessfulCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='text-black font-size-sm'>
                      {intl.formatMessage({
                        id: 'INVESTMENT.FEES',
                      })}
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageInvestmentFees'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='percentageInvestmentFees'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='percentageInvestmentFees'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedInvestmentFees'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${currencySymbol})`}</InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='fixedInvestmentFees'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedInvestmentFees'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='text-black font-size-sm'>
                      {/* {intl.formatMessage({
                                                id: 'INVESTMENT.FEES',
                                            })} */}
                      Gateway Fees
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageInvestmentFees'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='percentageGatewayFees'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='percentageGatewayFees'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          {/* <InputGroup.Prepend> */}
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedInvestmentFees'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${currencySymbol})`}</InputGroup.Text>
                          {/* </InputGroup.Prepend> */}
                          <Field
                            name='fixedGatewayFees'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedGatewayFees'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  {currentPermissions?.update_revenue_fees && (
                    <button
                      className='btn btn-secondary common-save-btn mb-2 mt-4'
                      type='button'
                      disabled={[
                        isReconnecting,
                        isConnecting,
                        isLoading,
                      ].includes(true)}
                      onClick={() => handleSubmitForm(values)}
                    >
                      {[isReconnecting, isConnecting, isLoading].includes(
                        true,
                      ) ? (
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        ></span>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  )}
                </Form>
              );

              // {set}
            }}
          </Formik>
        </Col>
      </Row>
    </CardBody>
  );
};
